"use client";

import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Container } from "@/components/ui/Container";
import { HeaderNav } from "@/components/HeaderNav";
import { Footer } from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";

const seoContent = {
  fr: {
    title: "Mentions Légales - Azurys | Agence web Paris",
    description: "Mentions légales du site Azurys. Informations sur la société, l'hébergement, la propriété intellectuelle et la protection des données.",
    backToHome: "Retour à l'accueil",
    pageTitle: "Mentions",
    pageTitleHighlight: "Légales"
  },
  en: {
    title: "Legal Notice - Azurys | Web Agency Paris",
    description: "Legal notice for Azurys website. Information about the company, hosting, intellectual property and data protection.",
    backToHome: "Back to home",
    pageTitle: "Legal",
    pageTitleHighlight: "Notice"
  }
};

function LegalContent() {
  const { lang } = useLanguage();
  const seo = seoContent[lang];

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://www.azurys.fr/legal" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <HeaderNav />
      
        <main className="pt-32 pb-20">
          <Container className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft size={16} />
                {seo.backToHome}
              </Link>

              <h1 className="text-4xl sm:text-5xl font-bold mb-8">
                {seo.pageTitle} <span className="gradient-text">{seo.pageTitleHighlight}</span>
              </h1>

              <div className="prose prose-invert max-w-none space-y-8">
                <section className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-4">1. Company Information</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">Company Name:</strong> Azurys<br />
                    <strong className="text-foreground">Legal Form:</strong> Auto-entreprise<br />
                    <strong className="text-foreground">Headquarters:</strong> Marseille, France<br />
                    <strong className="text-foreground">Email:</strong> contact@azurys.fr<br />
                    <strong className="text-foreground">Phone:</strong> +33 6 72 10 75 42
                  </p>
                </section>

                <section className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-4">2. Publication Director</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    The publication director of this website is Rayane Hemici, in their capacity as Founder.
                  </p>
                </section>

                <section className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-4">3. Hosting</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    This website is hosted by:<br />
                    <strong className="text-foreground">Host Name:</strong> Rayane H.<br />
                    <strong className="text-foreground">Address:</strong> 432 Montée des Acacias<br />
                    <strong className="text-foreground">Phone:</strong> 06 72 10 75 42
                  </p>
                </section>

                <section className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-4">4. Intellectual Property</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    The entire content of this website (texts, images, graphics, logo, icons, sounds, software, etc.) is the exclusive property of Azurys or its partners and is protected by French and international intellectual property laws.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-4">
                    Any reproduction, representation, modification, publication, or adaptation of all or part of the elements of the site, regardless of the means or process used, is prohibited without the prior written authorization of Azurys.
                  </p>
                </section>

                <section className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-4">5. Personal Data</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    In accordance with the General Data Protection Regulation (GDPR), you have the right to access, rectify, delete, and port your personal data, as well as the right to restrict processing and object to processing.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-4">
                    To exercise these rights or for any questions regarding the processing of your data, you can contact us at: contact@azurys.fr
                  </p>
                </section>

                <section className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-4">6. Cookies</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    This website may use cookies to improve the user experience. By continuing to browse this site, you agree to the use of cookies. You can configure your browser to refuse cookies at any time.
                  </p>
                </section>

                <section className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-4">7. Applicable Law</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    These legal notices are governed by French law. In the event of a dispute, the French courts will have exclusive jurisdiction.
                  </p>
                </section>
              </div>
            </motion.div>
          </Container>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default function Legal() {
  return (
    <LanguageProvider>
      <LegalContent />
    </LanguageProvider>
  );
}
